import { Link } from "react-router-dom";
import { Mountain, Leaf, Flame, Droplets, Clock } from "lucide-react";

const INGREDIENTS = [
    {
        name: "Hawaiian turmeric",
        role: "Brightens, warms, pulls color.",
    },
    {
        name: "Volcanic clay",
        role: "Pulls impurities, mineral-rich lather.",
    },
    {
        name: "Pacific sea salt",
        role: "Grounds, gently exfoliates, carries the ocean.",
    },
    {
        name: "Kukui nut oil",
        role: "Soft, silky, deeply hydrating.",
    },
    {
        name: "Cold-pressed coconut",
        role: "Clean cleanse, long-lasting bubble.",
    },
    {
        name: "Hibiscus infusion",
        role: "Natural rose-gold tone, antioxidant kiss.",
    },
];

const STEPS = [
    {
        n: "01",
        icon: Leaf,
        h: "Infuse the oils",
        p: "Botanicals are cold-infused into kukui and coconut oil for weeks. No shortcuts, no synthetic extracts.",
    },
    {
        n: "02",
        icon: Droplets,
        h: "Blend by hand",
        p: "Lye solution is combined with the infused oils. Trace is tracked by eye and feel — no machine mixing.",
    },
    {
        n: "03",
        icon: Mountain,
        h: "Fold the minerals",
        p: "Volcanic clay, sea salt, and turmeric are folded in, one spoon at a time. Marbling happens by hand.",
    },
    {
        n: "04",
        icon: Clock,
        h: "Rest and wait",
        p: "Bars rest in the cure room until the pH settles, the lather tightens, and the gold mark earns itself.",
    },
];

export default function About() {
    return (
        <div className="bg-cream" data-testid="about-page">
            {/* Hero — the craft, not the person */}
            <section className="relative py-20 md:py-28 bg-cream-warm overflow-hidden">
                <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-6">
                        <span className="divider-rule text-brown">
                            <Flame size={12} /> The Craft
                        </span>
                        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.98] text-forest mt-6">
                            How the soap
                            <br />
                            <span className="italic text-gold">
                                gets made.
                            </span>
                        </h1>
                        <p className="mt-8 text-brown leading-relaxed max-w-xl">
                            Small batches. Island ingredients you can trace
                            back to the soil. A cure time that refuses to be
                            rushed. Every bar is checked by hand before it
                            earns the gold mark.
                        </p>
                        <p className="mt-4 text-brown leading-relaxed max-w-xl">
                            No factory floor, no outsourced batching, no
                            fillers, no synthetic fragrance. If it's in the
                            bar, it came from the land, the ocean, or the
                            cure room.
                        </p>
                        <Link
                            to="/inventory"
                            className="btn-pill btn-primary mt-10"
                            data-testid="about-shop-btn"
                        >
                            See the First Drop
                        </Link>
                    </div>
                    <div className="md:col-span-6 grid grid-cols-2 gap-4">
                        <div className="aspect-[3/4] overflow-hidden rounded-sm">
                            <img
                                src="https://images.unsplash.com/photo-1584305574647-0cc949a2bb9f?auto=format&fit=crop&w=900&q=80"
                                alt="Handmade volcanic mineral soap"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="aspect-square overflow-hidden rounded-sm">
                                <img
                                    src="https://images.unsplash.com/photo-1600857544200-b2f666a9a2ec?auto=format&fit=crop&w=700&q=80"
                                    alt="Soap curing on rack"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="aspect-[4/3] overflow-hidden rounded-sm">
                                <img
                                    src="https://images.unsplash.com/photo-1607006344380-b6775a0824a7?auto=format&fit=crop&w=800&q=80"
                                    alt="Mineral clay bar"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ingredients */}
            <section className="py-20 md:py-28" data-testid="about-ingredients">
                <div className="max-w-6xl mx-auto px-5 md:px-10">
                    <span className="divider-rule text-brown">
                        <Leaf size={12} /> What Goes In
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl text-forest mt-5 max-w-3xl leading-[1.05]">
                        Six ingredients. All of them real.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {INGREDIENTS.map((ing, i) => (
                            <article
                                key={ing.name}
                                className="rounded-sm border border-forest/15 bg-cream-warm p-6 rise-in"
                                style={{ animationDelay: `${i * 80}ms` }}
                                data-testid={`about-ingredient-${ing.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            >
                                <h3 className="font-serif text-xl text-forest">
                                    {ing.name}
                                </h3>
                                <p className="mt-2 text-sm text-brown leading-relaxed">
                                    {ing.role}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process — four steps */}
            <section className="py-20 md:py-28 bg-cream-warm" data-testid="about-process">
                <div className="max-w-6xl mx-auto px-5 md:px-10">
                    <span className="divider-rule text-brown">
                        <Clock size={12} /> The Process
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl text-forest mt-5 max-w-3xl leading-[1.05]">
                        Four steps. No shortcuts.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <article
                                    key={s.n}
                                    className="rounded-sm bg-cream border border-forest/15 p-6 rise-in"
                                    style={{ animationDelay: `${i * 100}ms` }}
                                    data-testid={`about-step-${s.n}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                            {s.n}
                                        </p>
                                        <Icon size={18} className="text-gold" />
                                    </div>
                                    <h3 className="font-serif text-xl text-forest mt-3">
                                        {s.h}
                                    </h3>
                                    <p className="mt-2 text-sm text-brown leading-relaxed">
                                        {s.p}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Give back */}
            <section className="py-20 md:py-28" data-testid="about-giveback">
                <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
                    <span className="divider-rule text-brown justify-center">
                        10% · CyberKids Academy
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl text-forest mt-5 leading-[1.05]">
                        Craft lifts craft.
                    </h2>
                    <p className="mt-8 text-brown leading-relaxed text-lg">
                        10% of every bar sold funds CyberKids Academy —
                        code-literacy for island youth. The hands making
                        the soap today help build the hands making tomorrow.
                    </p>
                    <Link
                        to="/story"
                        className="mt-10 inline-flex btn-pill btn-ghost"
                        data-testid="about-story-cta"
                    >
                        Read Our Story →
                    </Link>
                </div>
            </section>
        </div>
    );
}
